<template>
  <div class="landing-page">
    <!-- Hero Section -->
    <section class="hero">
      <h1 class="hero-headline">
        The search engine for supply chain research.
      </h1>
      <p class="hero-subhead">
        Search across the full breadth of supply chain academic research, sourcing,
        procurement, planning, manufacturing, warehousing, transportation, logistics,
        distribution, retail operations, sustainability, risk, and resilience.
      </p>
      <div class="hero-search">
        <search-box show-examples autofocus />
      </div>
      <p class="hero-modes-hint">
        Switch to <strong>Ask</strong> to get answers synthesized from the catalog.
      </p>
    </section>
  </div>
</template>


<script setup>
import { useHead } from '@unhead/vue';
import { useStore } from 'vuex';

import SearchBox from '@/components/SearchBox.vue';
const store = useStore();

useHead({
  title: 'Supply Chain Research',
  titleTemplate: undefined
});

// Reset state on mount
store.commit('user/setActiveSearchId', null);
store.state.entityType = 'works';
</script>


<script>
export default {
  name: 'HomePage'
};
</script>


<style lang="scss" scoped>
.landing-page {
  background: #fff;
}

// Modes hint under search bar
.hero-modes-hint {
  margin-top: 16px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.65);
  text-align: center;

  strong {
    color: rgba(255, 255, 255, 0.9);
    font-weight: 600;
  }
}

// Ask CTA (legacy, no longer in template)
.hero-ask {
  margin-top: 24px;
  max-width: 600px;
}

.ask-cta {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: linear-gradient(135deg, rgba(210, 203, 254, 0.15), rgba(210, 203, 254, 0.05));
  border: 1px solid #d2cbfe;
  border-radius: 12px;
  text-decoration: none;
  color: #041708;
  transition: all 0.2s;

  &:hover {
    background: linear-gradient(135deg, rgba(210, 203, 254, 0.3), rgba(210, 203, 254, 0.15));
    border-color: #B8AFFE;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(107, 92, 231, 0.1);
  }
}

.ask-cta-icon {
  font-size: 24px;
  line-height: 1;
}

.ask-cta-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;

  strong {
    font-size: 15px;
    font-weight: 600;
  }
}

.ask-cta-sub {
  font-size: 13px;
  color: #52525B;
}

.ask-cta-arrow {
  font-size: 18px;
  color: #6B5CE7;
  transition: transform 0.2s;

  .ask-cta:hover & {
    transform: translateX(4px);
  }
}

// Hero Section
.hero {
  min-height: calc(100vh - var(--app-bar-height));
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  text-align: left;
  padding: 0 24px;
  margin-top: -40px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

.hero-headline {
  font-size: 56px;
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -0.03em;
  color: #0A0A0A;
  max-width: 750px;
  margin: 0 0 20px 0;
}

.hero-subhead {
  font-size: 19px;
  font-weight: 400;
  line-height: 1.65;
  color: #52525B;
  max-width: 600px;
  margin: 0 0 44px 0;
}

.hero-search {
  width: 100%;
  max-width: 600px;

  .search-box {
    border-color: #d2cbfe;
    box-shadow: 0 0 0 3px rgba(210, 203, 254, 0.3);
  }
}

.scroll-indicator {
  position: absolute;
  bottom: 24px;
  color: #A1A1AA;

  &:hover {
    color: #71717A;
  }
}

// Sections
.section {
  padding: 100px 24px;
  max-width: 1000px;
  margin: 0 auto;
}

.section-label {
  font-size: 14px;
  font-weight: 500;
  color: #A1A1AA;
  text-align: center;
  margin-bottom: 32px;
}

.section-header {
  font-size: 32px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #0A0A0A;
  text-align: center;
  margin-bottom: 56px;

  &::after {
    content: '';
    display: block;
    width: 60px;
    height: 3px;
    background-color: #d2cbfe;
    margin: 16px auto 0;
    border-radius: 2px;
  }
}

// Logos Section
.logos-section {
  padding-top: 40px;
  padding-bottom: 80px;
  border-bottom: 1px solid #F4F4F5;
}

.logos-row {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 40px;

  &.mobile {
    gap: 24px;

    .logo-img {
      height: 32px;

      &.tall {
        height: 38px;
      }

      &.short {
        height: 26px;
      }
    }
  }
}

.logo-img {
  height: 44px;
  filter: grayscale(1);
  opacity: 0.5;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }

  &.tall {
    height: 56px;
  }

  &.short {
    height: 36px;
  }
}

// Value Props Section (Big, Tidy, Open)
.value-props-section {
  padding-top: 120px;
  padding-bottom: 120px;
  background: linear-gradient(180deg, #FAFAFA 0%, #FFFFFF 100%);
  max-width: none;
}

.value-props-container {
  max-width: 680px;
  margin: 0 auto;
}

.value-prop {
  margin-bottom: 72px;

  &:last-child {
    margin-bottom: 0;
  }
}

.value-prop-title {
  font-size: 28px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #0A0A0A;
  margin: 0 0 16px 0;
  border-left: 4px solid #d2cbfe;
  padding-left: 16px;
}

.value-prop-text {
  font-size: 17px;
  line-height: 1.7;
  color: #52525B;
  margin: 0;
}

// Use Cases Section
.use-cases-section {
  padding-top: 120px;
  padding-bottom: 120px;
  max-width: none;

  .v-row {
    max-width: 1000px;
    margin: 0 auto;
  }
}

.use-case {
  padding-bottom: 24px;
}

.use-case-title {
  font-size: 18px;
  font-weight: 600;
  color: #0A0A0A;
  margin: 0 0 12px 0;
}

.use-case-text {
  font-size: 15px;
  line-height: 1.65;
  color: #52525B;
  margin: 0;
}

// FAQ Section
.faq-section {
  padding-top: 120px;
  padding-bottom: 140px;
  max-width: 700px;
}

.faq-list {
  // Nothing needed on container
}

.faq-item {
  border-bottom: 1px solid #E4E4E7;

  &:last-child {
    border-bottom: none;
  }
}

.faq-question {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 0;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  font-size: 16px;
  font-weight: 500;
  color: #0A0A0A;
  font-family: inherit;

  &:hover {
    color: #000;
  }

  &:focus {
    outline: none;
  }
}

.faq-icon {
  color: #A1A1AA;
  flex-shrink: 0;
  margin-left: 16px;
}

.faq-answer {
  padding-bottom: 24px;

  p {
    margin: 0;
    font-size: 15px;
    line-height: 1.7;
    color: #52525B;
  }

  a {
    color: #2563EB;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

// Responsive
@media (max-width: 960px) {
  .hero-headline {
    font-size: 44px;
  }

  .hero-subhead {
    font-size: 17px;
  }

  .section {
    padding: 80px 20px;
  }

  .section-header {
    font-size: 28px;
    margin-bottom: 48px;
  }

  .value-props-section,
  .use-cases-section {
    padding-top: 80px;
    padding-bottom: 80px;
  }

  .value-prop {
    margin-bottom: 56px;
  }

  .value-prop-title {
    font-size: 24px;
  }
}

@media (max-width: 600px) {
  .hero-headline {
    font-size: 36px;
  }

  .hero-subhead {
    font-size: 16px;
    max-width: 100%;
  }

  .section {
    padding: 64px 16px;
  }

  .section-header {
    font-size: 24px;
    margin-bottom: 40px;
  }

  .value-props-section,
  .use-cases-section,
  .faq-section {
    padding-top: 64px;
    padding-bottom: 64px;
  }

  .value-prop {
    margin-bottom: 48px;
  }

  .value-prop-title {
    font-size: 22px;
  }

  .value-prop-text {
    font-size: 16px;
  }
}
</style>
